import { CSSProperties, memo } from "react";
import { SpriteCell } from "./SpriteCell";
import {
  CELL_SIZE,
  HAT_Y_OFFSET,
  ItemFrame,
  RENDER_SCALE,
  RUN_BODY_ROW,
  SpriteRow,
  STANDING_STAFF_COL,
  WEAPON_OFFSET,
} from "./spriteData";
import type { AvatarConfig, Direction, EmoteType } from "./types";

interface CompositeAvatarProps {
  config: AvatarConfig;
  bodyCol: number;
  direction: Direction;
  scale?: number;
  weaponCol?: number;
  weaponRow?: "staff" | "items";
  weaponMirror?: boolean;
  weaponOffset?: { x: number; y: number };
  shieldOffset?: { x: number; y: number };
  emote?: EmoteType;
  displayName?: string;
}

export const CompositeAvatar = memo(function CompositeAvatar({
  config,
  bodyCol,
  direction,
  scale = RENDER_SCALE,
  weaponCol,
  weaponRow,
  weaponMirror = false,
  weaponOffset,
  shieldOffset,
  emote,
  displayName,
}: CompositeAvatarProps) {
  const cellPx = CELL_SIZE * scale;
  const facingRight = direction === "right";

  const containerStyle: CSSProperties = {
    position: "relative",
    width: cellPx,
    height: cellPx,
    transform: facingRight ? "scaleX(-1)" : undefined,
  };

  const hatStyle: CSSProperties = {
    bottom: HAT_Y_OFFSET[config.height] * scale,
    left: 0,
  };

  // Weapon
  const hasWeapon = config.weapon !== null;
  const isStaff = config.weapon === "staff";
  // Default to staff row for staff, items row for sword
  const resolvedRow = (weaponRow ?? (isStaff ? "staff" : "items")) === "staff"
    ? SpriteRow.Staff
    : SpriteRow.Items;
  const resolvedCol = weaponCol ?? (isStaff ? STANDING_STAFF_COL[config.height] : ItemFrame.SwordVert);
  const defaultOffset = isStaff ? WEAPON_OFFSET.staff : WEAPON_OFFSET.sword;
  const wOff = weaponOffset ?? { x: defaultOffset.x, y: defaultOffset.y };
  const weaponSalute = emote === "hand" && hasWeapon ? 2 : 0;
  const weaponStyle: CSSProperties = {
    left: wOff.x * scale,
    bottom: (wOff.y + weaponSalute) * scale,
  };

  // Shield
  const shieldBaseOffset = WEAPON_OFFSET.shield;
  const sOff = shieldOffset ?? { x: shieldBaseOffset.x, y: shieldBaseOffset.y };
  const shieldStyle: CSSProperties = {
    left: sOff.x * scale,
    bottom: sOff.y * scale,
  };

  return (
    <div style={containerStyle}>
      <SpriteCell
        row={RUN_BODY_ROW[config.height]}
        col={bodyCol}
        scale={scale}
        style={{ bottom: 0, left: 0 }}
      />

      {config.hatIndex !== null && (
        <SpriteCell
          row={SpriteRow.Hats}
          col={config.hatIndex}
          scale={scale}
          style={hatStyle}
        />
      )}

      {hasWeapon && (
        <SpriteCell
          row={resolvedRow}
          col={resolvedCol}
          scale={scale}
          mirror={weaponMirror}
          style={weaponStyle}
        />
      )}

      {config.hasShield && (
        <SpriteCell
          row={SpriteRow.Items}
          col={ItemFrame.Shield}
          scale={scale}
          style={shieldStyle}
        />
      )}

      {emote === "hand" && !hasWeapon && (
        <SpriteCell
          row={SpriteRow.Hats}
          col={2}
          scale={scale}
          style={{ bottom: HAT_Y_OFFSET[config.height] * scale, left: 1 * scale }}
        />
      )}

      {displayName && (
        <div
          className="avatar-nametag"
          style={{ transform: facingRight ? "translateX(-50%) scaleX(-1)" : undefined }}
        >
          {displayName}
        </div>
      )}
    </div>
  );
});
